import React from "react";
import { useLocation } from "react-router-dom";
import { useGoogleFonts } from "../../../hooks/useGoogleFonts";
import { DesignSystem, HeaderBlockProperties } from "../../../types/ThemeTypes";
import { defaultHeaderBlockProps } from "../../../config/defaultLandingConfig";
import styles from "./HeaderBlock.module.css";
import { isDarkColor } from "@/utils";
import { LogIn } from "react-feather";
import HamburgerIcon from "@/components/icons/HambugerIcon";

export interface HeaderBlockProps {
  isPublished?: boolean;
  properties?: HeaderBlockProperties;
  designSystem?: DesignSystem;
}

// Helper function to format border CSS value
const formatBorder = (width: string, color: string): string => {
  // Handle empty string or falsy values
  if (!width || width.trim() === "" || width === "0px" || width === "0") {
    return "none";
  }
  return `${width} solid ${color || "#000000"}`;
};

const HeaderBlock: React.FC<HeaderBlockProps> = ({
  isPublished = true,
  properties,
  designSystem,
}) => {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const location = useLocation();

  const props = {
    ...defaultHeaderBlockProps,
    ...properties,
  };

  useGoogleFonts([props.buttonFontFamily || "American Typewriter"]);

  const isProfilePage = location.pathname === "/profile";
  const buttonText = isProfilePage ? "Home" : props.buttonText;

  const handleLogoClick = () => {
    if (isPublished) {
      window.location.href = props.logoLink || "/";
    }
  };

  const handleButtonClick = () => {
    if (isPublished) {
      if (isProfilePage) {
        window.location.href = "/";
      } else {
        window.location.href = "/profile";
      }
    }
  };

  const handleHamburgerClick = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleMobileMenuButtonClick = () => {
    setIsMenuOpen(false);
    handleButtonClick();
  };

  const hamburgerIconColor = isDarkColor(props.backgroundColor)
    ? "#FFFFFF"
    : "#000000";

  return (
    <>
      <div
        className={styles.bb_minimalism_headerContainer}
        style={{
          // borderTop: formatBorder(props.borderTop, props.borderColor),
          // borderRight: formatBorder(props.borderRight, props.borderColor),
          // borderBottom: formatBorder(props.borderBottom, props.borderColor),
          // borderLeft: formatBorder(props.borderLeft, props.borderColor),
          backgroundColor: props.backgroundColor,
        }}
      >
        {/* Logo Section */}
        {props.logoEnabled && (
          <div
            className={styles.bb_minimalism_logoContainer}
            style={
              {
                "--logo-height": props.logoHeight || "40px",
                "--logo-height-mobile":
                  props.logoHeightMobile || props.logoHeight || "30px",
                "--logo-max-width": props.logoMaxWidth || "200px",
                "--logo-max-width-mobile":
                  props.logoMaxWidthMobile || props.logoMaxWidth || "150px",
              } as React.CSSProperties
            }
          >
            {props.logoImage ? (
              <img
                src={props.logoImage}
                alt={props.logoAlt?.trim() ?? ""}
                className={styles.bb_minimalism_logoImage}
                onClick={handleLogoClick}
              />
            ) : null}
          </div>
        )}

        {/* Button Section - Desktop */}
        {props.buttonEnabled && (
          <div className={styles.bb_minimalism_buttonContainer}>
            <button
              className={styles.bb_minimalism_styledButton}
              style={{
                fontFamily: props.buttonFontFamily,
                fontSize: `${props.buttonFontSize}px`,
                fontWeight: props.buttonBold ? "bold" : "normal",
                fontStyle: props.buttonItalic ? "italic" : "normal",
                textDecoration: props.buttonUnderline ? "underline" : "none",
                border: formatBorder(
                  props.buttonBorder,
                  props.buttonBorderColor
                ),
                backgroundColor: props.buttonBackgroundColor,
                color: props.buttonTextColor,
              }}
              onClick={handleButtonClick}
            >
              {buttonText}
            </button>
          </div>
        )}

        {/* LogIn / Hamburger Menu Button - Mobile */}
        {props.buttonEnabled && (
          <button
            className={styles.bb_minimalism_hamburgerButton}
            onClick={handleHamburgerClick}
            aria-label={isProfilePage ? "Toggle menu" : "Log in"}
          >
            {isProfilePage ? (
              HamburgerIcon(hamburgerIconColor, isMenuOpen)
            ) : (
              <LogIn size={24} color={hamburgerIconColor} strokeWidth={2} />
            )}
          </button>
        )}
      </div>

      {/* Mobile Menu Overlay */}
      {props.buttonEnabled && (
        <>
          <div
            className={`${styles.bb_minimalism_mobileMenuContainer} ${
              isMenuOpen ? styles.bb_minimalism_mobileMenuContainer_open : ""
            }`}
            style={{ backgroundColor: props.backgroundColor }}
          >
            {/* Mobile Menu Content */}
            <div className={styles.bb_minimalism_mobileMenuContent}>
              <button
                className={styles.bb_minimalism_mobileMenuButton}
                style={{
                  fontFamily: props.buttonFontFamily,
                  fontSize: `${props.buttonFontSize}px`,
                  fontWeight: props.buttonBold ? "bold" : "normal",
                  fontStyle: props.buttonItalic ? "italic" : "normal",
                  textDecoration: props.buttonUnderline ? "underline" : "none",
                  border: formatBorder(
                    props.buttonBorder,
                    props.buttonBorderColor
                  ),
                  backgroundColor: props.buttonBackgroundColor || "#ffffff",
                  color: props.buttonTextColor || "#333333",
                }}
                onClick={handleMobileMenuButtonClick}
              >
                {buttonText}
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default HeaderBlock;
